import { Check, Copy, ExternalLink, ShieldCheck } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { underV3, V3_ICON_TILE } from "@/components/ui/v3/flagged";
import {
  buildLaunchUrl,
  LAUNCH_TEMPLATE_ALIASES,
  type LaunchAction,
} from "@/lib/emr-launch";
import { useTemplateList } from "@/lib/template-queries";
import { cn } from "@/lib/utils";

const DEFAULT_TEMPLATE = "default";

export interface EmrLaunchCardProps {
  /** Origin the link points at; defaults to this app's origin. */
  origin?: string;
}

/** "Launch from your EMR": the toolbar link with a Copy button. */
export const EmrLaunchCard = ({ origin }: EmrLaunchCardProps): JSX.Element => {
  const { t } = useTranslation();
  const { templates } = useTemplateList();
  const [action, setAction] = useState<LaunchAction>("new-visit");
  const [template, setTemplate] = useState<string>(DEFAULT_TEMPLATE);
  const [copied, setCopied] = useState<"ok" | "failed" | null>(null);
  const linkRef = useRef<HTMLInputElement>(null);
  const ids = { link: useId(), template: useId(), opens: useId() };

  const url = useMemo(
    () =>
      buildLaunchUrl(origin ?? window.location.origin, {
        action,
        template:
          action === "new-visit" && template !== DEFAULT_TEMPLATE
            ? template
            : undefined,
        source: "emr",
      }),
    [origin, action, template],
  );

  useEffect(() => {
    setCopied(null);
  }, [url]);
  useEffect(() => {
    if (copied !== "ok") return;
    const timer = setTimeout(() => setCopied(null), 2500);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied("ok");
    } catch {
      // Clipboard blocked: select the text so the user can copy it.
      linkRef.current?.select();
      setCopied("failed");
    }
  };

  const actions: { value: LaunchAction; label: string; hint: string }[] = [
    {
      value: "new-visit",
      label: t("settings.emrLaunch.opens.newVisit"),
      hint: t("settings.emrLaunch.opens.newVisitHint"),
    },
    {
      value: "latest-visit",
      label: t("settings.emrLaunch.opens.latestVisit"),
      hint: t("settings.emrLaunch.opens.latestVisitHint"),
    },
  ];

  return (
    <section
      aria-labelledby="emr-launch-title"
      className="bg-background sm:rounded-[15px] sm:border sm:border-border sm:p-6"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full bg-v3-brand-tint text-v3-brand sm:size-11",
            underV3(V3_ICON_TILE),
          )}
        >
          <ExternalLink aria-hidden="true" className="size-5" />
        </div>
        <div className="min-w-0 space-y-1">
          <h3 id="emr-launch-title" className="text-lg font-bold leading-snug">
            {t("settings.emrLaunch.title")}
          </h3>
          <p className="text-sm text-muted-foreground sm:text-base">
            {t("settings.emrLaunch.subtitle")}
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-semibold">
            {t("settings.emrLaunch.opens.label")}
          </legend>
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
            {actions.map((item) => (
              <label
                key={item.value}
                className={cn(
                  "flex min-h-11 cursor-pointer items-center justify-center rounded-md px-3 text-sm font-medium transition-colors has-focus-visible:ring-2 has-focus-visible:ring-ring sm:min-h-9",
                  action === item.value
                    ? "bg-background text-foreground shadow-xs"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <input
                  type="radio"
                  name={ids.opens}
                  value={item.value}
                  checked={action === item.value}
                  onChange={() => setAction(item.value)}
                  className="sr-only"
                />
                {item.label}
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {actions.find((item) => item.value === action)?.hint}
          </p>
        </fieldset>

        <div className="space-y-2">
          <label htmlFor={ids.template} className="text-sm font-semibold">
            {t("settings.emrLaunch.template.label")}
          </label>
          <Select
            value={template}
            onValueChange={setTemplate}
            disabled={action !== "new-visit"}
          >
            <SelectTrigger
              id={ids.template}
              className="min-h-11 w-full sm:min-h-9"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DEFAULT_TEMPLATE}>
                {t("settings.emrLaunch.template.default")}
              </SelectItem>
              <SelectItem value="soap">
                {LAUNCH_TEMPLATE_ALIASES.soap}
              </SelectItem>
              <SelectItem value="specialist">
                {LAUNCH_TEMPLATE_ALIASES.specialist}
              </SelectItem>
              <SelectItem value="dictation">
                {LAUNCH_TEMPLATE_ALIASES.dictation}
              </SelectItem>
              {templates.length > 0 && (
                <SelectGroup>
                  <SelectLabel>
                    {t("settings.emrLaunch.template.yours")}
                  </SelectLabel>
                  {templates.map((item) => (
                    <SelectItem key={item.public_id} value={item.public_id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {action === "new-visit"
              ? t("settings.emrLaunch.template.hint")
              : t("settings.emrLaunch.template.notUsed")}
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <label htmlFor={ids.link} className="text-sm font-semibold">
          {t("settings.emrLaunch.link.label")}
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id={ids.link}
            ref={linkRef}
            readOnly
            value={url}
            onFocus={(event) => event.currentTarget.select()}
            className="min-h-11 w-full min-w-0 flex-1 rounded-md border border-input bg-muted/60 px-3 font-mono text-[13px] text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:min-h-10"
          />
          <Button
            type="button"
            onClick={() => void copy()}
            className="min-h-11 shrink-0 px-5 sm:min-h-10"
            aria-live="polite"
          >
            {copied === "ok" ? (
              <Check aria-hidden="true" />
            ) : (
              <Copy aria-hidden="true" />
            )}
            {copied === "ok"
              ? t("settings.emrLaunch.link.copied")
              : t("settings.emrLaunch.link.copy")}
          </Button>
        </div>
        {copied === "failed" && (
          <p role="alert" className="text-xs text-destructive">
            {t("settings.emrLaunch.link.copyFailed")}
          </p>
        )}
      </div>

      <div className="mt-6 space-y-3">
        <h4 className="text-sm font-semibold">
          {t("settings.emrLaunch.steps.title")}
        </h4>
        <ol className="space-y-3">
          {(["one", "two", "three", "four"] as const).map((step, index) => (
            <li key={step} className="flex gap-3 text-sm">
              <span
                aria-hidden="true"
                className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
              >
                {index + 1}
              </span>
              <span className="pt-0.5 text-foreground/90">
                {t(`settings.emrLaunch.steps.${step}`)}
              </span>
            </li>
          ))}
        </ol>
        <p className="text-xs text-muted-foreground">
          {t("settings.emrLaunch.steps.emrs")}
        </p>
      </div>

      <div className="mt-5 flex gap-3 rounded-xl bg-v3-ok-tint p-3 font-v3 text-[13.5px] leading-snug text-v3-ok-text">
        <ShieldCheck aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
        <p>{t("settings.emrLaunch.privacy")}</p>
      </div>
    </section>
  );
};

export const EmrLaunchSettings = (): JSX.Element => (
  <section className="space-y-5">
    <EmrLaunchCard />
  </section>
);
